import dynamic from 'next/dynamic';
import { Collapsible } from '@product/Collapsible/Collapsible';
import { KoplaTeaser } from '@product/KoplaTeaser/KoplaTeaser';
import { Tabs } from '@product/Tabs/Tabs';
import { useBreakpoints } from '@hooks/useBreakpoints';
import { ProductHelper } from '@models/graphql';
import type { ContentItem } from '@models/data';
import { useTranslation } from 'next-i18next';
import { ProductDescriptionTab } from './tabs/ProductDescriptionTab';
import { TechnicalDataTab } from './tabs/TechnicalDataTab/TechnicalDataTab';
import { SuitableProductsTab } from './tabs/SuitableProductsTab';
import type { SuitableProductProps } from '@product/SuitableProduct/SuitableProduct';
import { sortByAnotherArray } from '@libs/comparer.helper';
import { ProductSelectionTab } from './tabs/ProductSelectionTab/ProductSelectionTab';
import { useProductPageStore } from '@hooks/states/useProductPageStore';
import {
  DEFAULT_PRODUCT_TABS,
  DRYLIN_LINEAR_TECHNOLOGY_PRODUCT_TABS,
  ECHAIN_PRODUCT_TABS,
  PRODUCT_TABS_KEY,
  RC_PRODUCT_TABS,
  TRIFLEX_PRODUCT_TABS,
} from '@product/ProductTabs/ProductTabsTypes';
import type { ProductAttribute } from '@libs/attribute.helper';
import { getAttributeValue } from '@libs/attribute.helper';
import { CategoryHelper } from '@libs/category.helper';
import { getLocale, ONLINE_TOOLS_TEASER_US_SPECIFIC_IDS } from '@libs/constants';
import { useRouter } from 'next/router';

const DownloadTab = dynamic<Record<string, never>>(
  () => import('./tabs/DownloadTab/DownloadTab').then(mod => mod.DownloadTab),
  {
    ssr: false,
  }
);

const ApplicationExamplesTeaser = dynamic<Record<string, never>>(
  () =>
    import('../../common/ApplicationExamplesTeaser/ApplicationExamplesTeaser').then(
      mod => mod.ApplicationExamplesTeaser
    ),
  {
    ssr: false,
  }
);

export interface ProductTabsProps {
  productDetails: string;
  suitableProducts: SuitableProductProps[] | null;
  showOnlineTools?: boolean;
}

export const ProductTabs = ({ productDetails, suitableProducts, showOnlineTools = true }: ProductTabsProps) => {
  const { t } = useTranslation('product/productDetails');
  const { md, available } = useBreakpoints();
  const locale = getLocale(useRouter());
  const currentProduct = useProductPageStore(state => state.currentProduct);
  // TODO : onlineToolsDefinition filtering Should be removed within COM-8364
  const onlineToolsDefinition = useProductPageStore(state =>
    locale === 'en-US'
      ? state.onlineToolsDefinition.filter(teaser => ONLINE_TOOLS_TEASER_US_SPECIFIC_IDS.includes(teaser.id))
      : state.onlineToolsDefinition
  );
  const onlineTools = ProductHelper.getOnlineTools(currentProduct, onlineToolsDefinition);
  const primaryCategoryCode = useProductPageStore(state => state.primaryCategoryCode);
  const certificateIcons = useProductPageStore(state => state.certificateIcons);
  const doApplicationExamplesExist = useProductPageStore(state => state.doApplicationExamplesExist);
  const isDownloadTabEnabled = currentProduct.meta?.existDownloadAssets;
  const isTechnicalDataTabEnabled = currentProduct.meta?.existTechnicalData;

  let productTabsConfig: PRODUCT_TABS_KEY[] = DEFAULT_PRODUCT_TABS;
  if (CategoryHelper.isReadycable(primaryCategoryCode)) {
    productTabsConfig = RC_PRODUCT_TABS;
  }
  if (CategoryHelper.isEchain(primaryCategoryCode)) {
    productTabsConfig = ECHAIN_PRODUCT_TABS;
  }
  if (CategoryHelper.isDylinLinearTechnology(primaryCategoryCode)) {
    productTabsConfig = DRYLIN_LINEAR_TECHNOLOGY_PRODUCT_TABS;
  }
  if (CategoryHelper.isTriflex(primaryCategoryCode)) {
    productTabsConfig = TRIFLEX_PRODUCT_TABS;
  }

  // TODO: Load commented Tabs dependent on data, when other products are supported
  const tabs: ContentItem[] = [
    {
      key: PRODUCT_TABS_KEY.DESCRIPTION,
      title: t('PRODUCT_DETAILS.DESCRIPTION_TITLE'),
      content: (
        <ProductDescriptionTab
          productDetails={productDetails}
          product={currentProduct}
          certificateIcons={certificateIcons}
          selectionProArguments={getAttributeValue<string>(
            currentProduct.attributes?.when_to_use as ProductAttribute<string>
          )}
          selectionConArguments={getAttributeValue<string>(
            currentProduct.attributes?.when_not_to_use as ProductAttribute<string>
          )}
        />
      ),
    },
    ...(isTechnicalDataTabEnabled
      ? [
          {
            key: PRODUCT_TABS_KEY.TECHNICAL_DETAILS,
            title: t('PRODUCT_DETAILS.TECHNICAL_DATA'),
            content: <TechnicalDataTab />,
          },
        ]
      : []),
    ...(suitableProducts && suitableProducts.length > 0
      ? [
          {
            key: PRODUCT_TABS_KEY.SUITABLE_PRODUCTS,
            title: t('PRODUCT_DETAILS.SUITABLE_PRODUCTS_TITLE'),
            content: <SuitableProductsTab suitableProducts={suitableProducts} />,
          },
        ]
      : []),
    ...(showOnlineTools && onlineTools && onlineTools.length > 0
      ? [
          {
            key: PRODUCT_TABS_KEY.ONLINE_TOOLS,
            title: t('PRODUCT_DETAILS.ONLINE_TOOLS_TITLE'),
            content: <KoplaTeaser tools={onlineTools} />,
          },
        ]
      : []),
    ...(doApplicationExamplesExist
      ? [
          {
            key: PRODUCT_TABS_KEY.APPLICATION_EXAMPLES,
            title: t('PRODUCT_DETAILS.EXAMPLES_TITLE'),
            content: <ApplicationExamplesTeaser />,
          },
        ]
      : []),
    ...(isDownloadTabEnabled
      ? [
          {
            key: PRODUCT_TABS_KEY.DOWNLOAD_ASSETS,
            title: t('PRODUCT_DETAILS.DOWNLOADS'),
            content: <DownloadTab />,
          },
        ]
      : []),
    ...(CategoryHelper.isEchain(primaryCategoryCode)
      ? [
          {
            key: PRODUCT_TABS_KEY.PRODUCT_SELECTION,
            title: t('PRODUCT_DETAILS.PRODUCT_SELECTION_TITLE'),
            content: <ProductSelectionTab />,
          },
        ]
      : []),
  ]
    .sort((a, b) =>
      sortByAnotherArray<PRODUCT_TABS_KEY>(a.key, b.key, productTabsConfig, {
        moveMissingItems: 'end',
      })
    )
    .filter(Boolean);

  return (
    <>
      <div className="hidden md:block">{!available || md ? <Tabs items={tabs} /> : null}</div>
      <div className="md:hidden">{!available || !md ? <Collapsible items={tabs} /> : null}</div>
    </>
  );
};
